import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Award,
  Users,
  Leaf,
  Target,
  ArrowRight,
} from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: Award,
      title: "Quality Craftsmanship",
      description: "We take pride in every project, ensuring the highest standards of workmanship.",
    },
    {
      icon: Users,
      title: "Customer Focus",
      description: "Your satisfaction is our priority. We listen, communicate, and deliver.",
    },
    {
      icon: Leaf,
      title: "Environmental Responsibility",
      description: "We use sustainable practices and eco-friendly solutions whenever possible.",
    },
    {
      icon: Target,
      title: "Attention to Detail",
      description: "From design to execution, we focus on getting every detail right.",
    },
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-[#FAFAF8]">
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-[#2D5A27] py-20 md:py-28">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1
                data-testid="about-page-headline"
                className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold mb-6"
              >
                About CasaCurate Store
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                Your trusted local landscaping partner in Los Angeles, dedicated to creating beautiful outdoor spaces since 2015.
              </p>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="text-[#C2410C] font-medium text-sm mb-4 block">Our Story</span>
                <h2 className="font-heading text-3xl md:text-4xl text-[#1A2C22] font-bold mb-6">
                  A Decade of Growing Dreams
                </h2>
                <div className="space-y-4 text-[#4B5563] leading-relaxed">
                  <p>
                    CasaCurate Store was founded in 2015 with a simple mission: to help Los Angeles homeowners create beautiful, functional outdoor spaces they can enjoy with their families.
                  </p>
                  <p>
                    What started as a small team with a passion for plants and design has grown into a full-service landscaping company serving residential clients throughout the greater Los Angeles area.
                  </p>
                  <p>
                    Our approach combines traditional landscaping expertise with modern design principles and sustainable practices. We believe that every property has potential, and we're here to help you realize yours.
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1758524053982-dc0fc7cd651f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGhvbWVvd25lciUyMGNvdXBsZSUyMGluJTIwZ2FyZGVufGVufDB8fHx8MTc3MjcwNjI1NHww&ixlib=rb-4.1.0&q=85"
                  alt="Happy homeowners"
                  className="rounded-3xl shadow-lg"
                />
                <div className="absolute -bottom-6 -left-6 bg-[#2D5A27] text-white rounded-2xl p-6 hidden md:block">
                  <div className="text-center">
                    <span className="font-heading text-4xl font-bold">10+</span>
                    <p className="text-white/80 text-sm mt-1">Years of Experience</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-[#C2410C] font-medium text-sm mb-4 block">Our Values</span>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1A2C22] font-bold">
                What Drives Us
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  data-testid={`value-${index}`}
                  className="text-center p-6"
                >
                  <div className="w-16 h-16 bg-[#F3F4F1] rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <value.icon className="w-8 h-8 text-[#2D5A27]" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A2C22] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-[#4B5563] text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Info Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-sm">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <span className="text-[#C2410C] font-medium text-sm mb-4 block">Contact Information</span>
                  <h2 className="font-heading text-3xl md:text-4xl text-[#1A2C22] font-bold mb-8">
                    Get In Touch
                  </h2>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#F3F4F1] rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#2D5A27]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1A2C22]">CasaCurate Store</p>
                        <p className="text-[#4B5563]">
                          1513 Sunset Boulevard<br />
                          Los Angeles, CA 90027
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#F3F4F1] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-[#2D5A27]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1A2C22]">Phone</p>
                        <a href="tel:+17711871113" className="text-[#4B5563] hover:text-[#2D5A27]">
                          +1-771-1871-113
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#F3F4F1] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-[#2D5A27]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1A2C22]">Email</p>
                        <a href="mailto:info@casacurate.store" className="text-[#4B5563] hover:text-[#2D5A27]">
                          info@casacurate.store
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#F3F4F1] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-[#2D5A27]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1A2C22]">Business Hours</p>
                        <p className="text-[#4B5563]">
                          Mon - Fri: 7:00 AM - 6:00 PM<br />
                          Sat: 8:00 AM - 4:00 PM<br />
                          Sun: Closed
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <h3 className="font-heading text-2xl text-[#1A2C22] font-semibold mb-4">
                      Ready to Transform Your Space?
                    </h3>
                    <p className="text-[#4B5563] mb-8">
                      Contact us today for a free consultation and estimate.
                    </p>
                    <Link to="/contact">
                      <Button
                        data-testid="about-cta"
                        className="bg-[#C2410C] hover:bg-[#9A3412] text-white h-14 px-10 text-lg rounded-full"
                      >
                        Get Free Estimate
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
